import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { TextField } from '@mui/material';
import Dialog from '../../Dialog';
import { ColumnFlexBox } from '../../FlexBox';
import useTranslate from '../../../Hooks/useTranslate';
import { widgetRenderer, WIDGET_TYPES } from '../detail/WidgetRegistry';
import { presetOfRegion } from '../detail/useWidgetPresets';

/** What a preset may be named, which is what a saved layout will hold. */
const NAME_RULE = /^[A-Za-z0-9_]+$/;

/**
 * Names the region being saved as a preset.
 *
 * A preset is the region without its place on the page, so what there is to say is what a layout
 * will name it by and what the palette will call it. A name already taken replaces that preset
 * rather than being refused, since saving over one is how it is edited.
 *
 * @param {Object} props - Component props.
 * @param {Boolean} props.open - Whether the dialog is open.
 * @param {Function} props.close - Closes it, discarding what was typed.
 * @param {Object} props.region - The region being saved.
 * @param {Array} props.presets - The presets already saved, for the name that replaces one.
 * @param {Function} props.onSave - Receives the row to write.
 *
 * @returns {React.JSX.Element}
 */
const PresetDialog = function PresetDialog({
  open = false,
  close = () => {},
  region = null,
  presets = [],
  onSave = () => {},
}) {
  const intl = useIntl();
  const translate = useTranslate();

  const [name, setName] = useState('');
  const [label, setLabel] = useState('');

  useEffect(() => {
    if (!open) return;

    // Named after the region, which is what it was called on the page it was arranged on.
    setName(String(region?.id ?? '').replace(/[^A-Za-z0-9_]/g, ''));
    setLabel(
      region?.label ??
        WIDGET_TYPES.find(({ widget }) => widget === region?.widget)?.label ??
        ''
    );
  }, [open, region]);

  const replaced = presets.find((preset) => preset.name === name) ?? null;
  const valid = NAME_RULE.test(name);

  return (
    <Dialog
      id="preset-dialog"
      title={intl.messages.SAVE_AS_PRESET}
      open={open}
      onClose={close}
      disableSubmit={!valid}
      onSubmit={() => {
        onSave({
          id: replaced?.id ?? null,
          name,
          label: label || name,
          renderer: widgetRenderer(region?.widget ?? 'fields'),
          jsonStructure: JSON.stringify(presetOfRegion(region)),
        });
        close();
      }}
      submitLabel="SAVE"
      width="xs"
      fullWidth
      dividers
    >
      <ColumnFlexBox gap="16px" sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          size="small"
          label={intl.messages.PRESET_NAME}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={Boolean(name) && !valid}
          helperText={
            replaced ? intl.messages.PRESET_REPLACES : intl.messages.PRESET_RULE
          }
        />
        <TextField
          size="small"
          label={intl.messages.LABEL}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          helperText={label ? translate(label) : ''}
        />
      </ColumnFlexBox>
    </Dialog>
  );
};

export default PresetDialog;
